import React, { ElementType, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMemberExperienceAuth } from '@guildeducationinc/auth-frontend-tools';
import { OfferingHit } from 'src/queries/user';
import { trackEvent, trackProgramClick, trackViewedObjects } from 'src/services/analytics';
import { isBookmarked, setPosition } from 'src/utilities/offeringHitsUtility';
import { NoResultsPlaceholder } from '@src/components/NoResultsPlaceholder';
import { useBookmarks } from 'src/components/Providers/BookmarksProvider';
import { useBookmarkActions } from 'src/hooks/useBookmarkActions';
import { useSearchContext } from 'src/components/Providers/SearchProvider';
import { useHits } from 'react-instantsearch-hooks-web';
import { useDebounce } from 'src/utilities/inputUtility';
import { ProgramCard } from '@src/remotes/ProgramCard';
import { ProgramCardProvider } from '@src/remotes/ProgramCardProvider';
import { ShortProgramKind } from '@src/graphqlTypes/catalogServiceTypes';
import { getUserValuesForPath } from '@src/constants/getUserValuesForPath';
import { useLimitedCatalog } from '@src/hooks/useLimitedCatalog';
import { ProductUsableComponents, useProductUsable } from '../Providers/ProductUsableProvider';
import styles from './ResultsList.module.scss';

export const ResultsList: React.FC = () => {
	const { employerUuid, isMemberServicesRoute, memberUuid, planUuid } = useSearchContext();
	const { userFromIdToken: user, getAccessToken, getIdToken } = useMemberExperienceAuth();
	const authUser = getUserValuesForPath(user);
	const { bookmarks } = useBookmarks();
	const { updateBookmark } = useBookmarkActions();
	const { results } = useHits();
	const { componentLoaded } = useProductUsable();
	const programHits: unknown[] = results.hits;
	const debounceResults: unknown[] = useDebounce(programHits, 200);
	const { limitedCatalogEnabled } = useLimitedCatalog();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [results.page]);

	useEffect(() => {
		if (programHits !== debounceResults) {
			const programIds = (programHits as OfferingHit[]).map(hit => hit.id.toString());
			const objectIds = (programHits as OfferingHit[]).map(hit => `${hit.id}-${planUuid}`);
			trackViewedObjects({
				index: results._rawResults[0].indexUsed,
				objectIds,
				queryId: results._rawResults[0].queryID,
				userUuid: authUser.id,
			});
			trackEvent('allResults', {
				abTestId: results._rawResults[0]?.abTestID,
				abTestVariantId: results._rawResults[0]?.abTestVariantID,
				filters: results._rawResults[0]?.params,
				indexUsed: results._rawResults[0].indexUsed,
				nbHits: results.nbHits,
				objectIds,
				programIds,
				typedTerm: results._rawResults[0].query,
				userRoles: user.roles,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [programHits, debounceResults]);

	const onClick = (event, hit, hitIndex) => {
		if (event.target.closest('button')?.id.includes('popover-button')) {
			trackEvent('Clickable', {
				offeringID: hit.id.toString(),
				tag: 'program_card',
				text: event.target.closest('button').innerText,
				type: 'popover',
			});
		} else {
			trackProgramClick({
				position: setPosition(results.page, hitIndex),
				programId: hit.id.toString(),
				objectId: `${hit.id}-${planUuid}`,
				userUuid: authUser.id,
				index: results?._rawResults[0].index,
				queryId: results?._rawResults[0].queryID,
			});
		}
	};

	const formattedProgramHits = (programHits as OfferingHit[]).map(hit => ({
		...hit,
		nextDate: hit.nextDate,
		shortProgramKind: ShortProgramKind[hit.shortProgramKind?.toUpperCase()],
	}));

	const programUuids = formattedProgramHits.map(hit => hit.uuid);
	const hasNoResults =
		results.nbHits === 0 && (results.query !== '' || results.disjunctiveFacets.length !== 0);

	useEffect(() => {
		if (hasNoResults) {
			componentLoaded(ProductUsableComponents.RESULTS_LIST);
		}
	}, [hasNoResults, componentLoaded]);

	return (
		<div data-testid='filter-results-container'>
			{hasNoResults ? (
				<NoResultsPlaceholder />
			) : (
				<div className={styles.resultsListWrapper}>
					<ProgramCardProvider
						getAccessToken={getAccessToken}
						getIdToken={getIdToken}
						onLoaded={() => componentLoaded(ProductUsableComponents.RESULTS_LIST)}
						programUuids={programUuids}
						user={authUser}
					>
						{formattedProgramHits.map((hit, index) => {
							return (
								<ProgramCard
									LinkComponent={Link as unknown as ElementType}
									bookmarked={isBookmarked(hit.id.toString(), bookmarks)}
									className={styles.programCard}
									employerName={authUser.employer}
									handleBookmark={() => updateBookmark(hit.id.toString())}
									hideBookmark={limitedCatalogEnabled}
									key={hit.id}
									offeringLocation='gallery'
									onClick={event => onClick(event, hit, index)}
									product='catalog'
									programUuid={hit.uuid}
									to={{
										pathname: `${
											isMemberServicesRoute ? `/memberservices/${employerUuid}/${memberUuid}` : ''
										}/programs/${hit.id}/?queryID=${results?._rawResults[0].queryID}`,
										state: { fromGuildCatalog: true },
									}}
								/>
							);
						})}
					</ProgramCardProvider>
				</div>
			)}
		</div>
	);
};

export default ResultsList;
